/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

import com.garmin.fit.types.*
import kotlin.time.Instant

/**
 * The FIT `hsa_body_battery_data` message.
 *
 * Body battery data used for HSA custom data logging
 *
 * A thin typed view over [Mesg]: the accessors below read and write the same
 * numbered fields the untyped API exposes, so nothing is hidden by using them.
 */
public open class HsaBodyBatteryDataMesg : Mesg {
    public constructor() : super("HsaBodyBatteryData", Profile.MesgNum.HSA_BODY_BATTERY_DATA)

    public constructor(mesg: Mesg) : super(mesg)

    internal constructor(mesg: Mesg, adopt: Boolean) : super(mesg, adopt)

    /** timestamp, in s. */
    public var timestamp: Instant?
        get() = (getFieldValue(TIMESTAMP_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(TIMESTAMP_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    /** processing_interval, in s. Processing interval length in seconds */
    public var processingInterval: UShort?
        get() = getFieldValue(PROCESSING_INTERVAL_FIELD_NUM) as? UShort
        set(value) = setFieldValue(PROCESSING_INTERVAL_FIELD_NUM, value)

    /** Number of values stored in level. */
    public val numLevel: Int
        get() = getField(LEVEL_FIELD_NUM)?.numValues ?: 0

    /** level, in percent. Body battery level: [0,100] Blank: -16 */
    public var level: List<Byte?>?
        get() {
            val field = getField(LEVEL_FIELD_NUM) ?: return null
            return List(field.numValues) { getLevel(it) }
        }
        set(value) {
            removeField(LEVEL_FIELD_NUM)
            value?.forEachIndexed { index, element -> setLevel(index, element) }
        }

    public fun getLevel(index: Int): Byte? =
        getFieldValue(LEVEL_FIELD_NUM, index) as? Byte

    public fun setLevel(index: Int, value: Byte?) {
        setFieldValue(LEVEL_FIELD_NUM, value, index)
    }

    /** Number of values stored in charged. */
    public val numCharged: Int
        get() = getField(CHARGED_FIELD_NUM)?.numValues ?: 0

    /** charged. Body battery charged value */
    public var charged: List<Short?>?
        get() {
            val field = getField(CHARGED_FIELD_NUM) ?: return null
            return List(field.numValues) { getCharged(it) }
        }
        set(value) {
            removeField(CHARGED_FIELD_NUM)
            value?.forEachIndexed { index, element -> setCharged(index, element) }
        }

    public fun getCharged(index: Int): Short? =
        getFieldValue(CHARGED_FIELD_NUM, index) as? Short

    public fun setCharged(index: Int, value: Short?) {
        setFieldValue(CHARGED_FIELD_NUM, value, index)
    }

    /** Number of values stored in uncharged. */
    public val numUncharged: Int
        get() = getField(UNCHARGED_FIELD_NUM)?.numValues ?: 0

    /** uncharged. Body battery uncharged value */
    public var uncharged: List<Short?>?
        get() {
            val field = getField(UNCHARGED_FIELD_NUM) ?: return null
            return List(field.numValues) { getUncharged(it) }
        }
        set(value) {
            removeField(UNCHARGED_FIELD_NUM)
            value?.forEachIndexed { index, element -> setUncharged(index, element) }
        }

    public fun getUncharged(index: Int): Short? =
        getFieldValue(UNCHARGED_FIELD_NUM, index) as? Short

    public fun setUncharged(index: Int, value: Short?) {
        setFieldValue(UNCHARGED_FIELD_NUM, value, index)
    }

    public companion object {
        public const val TIMESTAMP_FIELD_NUM: UByte = 253u
        public const val PROCESSING_INTERVAL_FIELD_NUM: UByte = 0u
        public const val LEVEL_FIELD_NUM: UByte = 1u
        public const val CHARGED_FIELD_NUM: UByte = 2u
        public const val UNCHARGED_FIELD_NUM: UByte = 3u

        /**
         * The profile definition of every field of this message, with no values.
         *
         * Built once when this class is first touched, and handed out only as
         * copies, so the decoder pays for a message type it never meets nothing.
         */
        internal val PROTOTYPE_FIELDS: Map<Int, Field> = buildPrototypeFields()

        private fun buildPrototypeFields(): Map<Int, Field> {
            val fields = LinkedHashMap<Int, Field>()
            fields[253] = Field("Timestamp", 253u, BaseType.UINT32, 1.0, 0.0, "s", false)
            fields[0] = Field("ProcessingInterval", 0u, BaseType.UINT16, 1.0, 0.0, "s", false)
            fields[1] = Field("Level", 1u, BaseType.SINT8, 1.0, 0.0, "percent", false)
            fields[2] = Field("Charged", 2u, BaseType.SINT16, 1.0, 0.0, "", false)
            fields[3] = Field("Uncharged", 3u, BaseType.SINT16, 1.0, 0.0, "", false)
            return fields
        }
    }
}
